import { format } from "date-fns";
import { parseIsoDateWithinRange } from "utils/dateValidation";
import type { CheckAppointmentConflictsInput } from "./checkAppointmentConflicts";
import { localDateAndTimeToUtcIso } from "./appointmentTimeUtils";

type AssigneeOption = { value: number | string; label: string };

/** Same HHMM encoding as AppointmentModal submit payload. */
export const toApiDurationString = (duration: unknown): string | null => {
  if (duration == null || duration === "") return null;
  const digits = String(duration).replace(/[^0-9]/g, "");
  if (!digits) return null;
  return digits.padStart(4, "0").slice(-4);
};

export function buildAppointmentConflictPayload(
  params: {
    assignee: AssigneeOption[] | null | undefined;
    date: string;
    startTimeLocal: string;
    endTimeLocal?: string;
    duration: string;
    timezone: string;
    excludeAppointmentId?: number | string | null;
  },
  dateOverride?: string
): CheckAppointmentConflictsInput | null {
  const assigneeIds = (Array.isArray(params.assignee) ? params.assignee : [])
    .map((a) => Number(a.value))
    .filter((id) => Number.isInteger(id) && id > 0);

  if (assigneeIds.length === 0 || !params.startTimeLocal?.trim()) {
    return null;
  }

  const parsedDate = parseIsoDateWithinRange(dateOverride ?? params.date);
  if (!parsedDate) return null;

  const dateStr = format(parsedDate, "yyyy-MM-dd");
  const start_time_utc = localDateAndTimeToUtcIso(
    dateStr,
    params.startTimeLocal,
    params.timezone
  );

  if (!start_time_utc) return null;

  const payload: CheckAppointmentConflictsInput = {
    assigneeIds,
    date: dateStr,
    start_time_utc,
    duration: toApiDurationString(params.duration),
  };

  if (params.endTimeLocal?.trim()) {
    const end_time_utc = localDateAndTimeToUtcIso(
      dateStr,
      params.endTimeLocal,
      params.timezone
    );
    if (end_time_utc) {
      payload.end_time_utc = end_time_utc;
    }
  }

  const excludeId =
    params.excludeAppointmentId != null && params.excludeAppointmentId !== ""
      ? Number(params.excludeAppointmentId)
      : undefined;

  if (excludeId != null && Number.isFinite(excludeId) && excludeId > 0) {
    payload.excludeAppointmentId = excludeId;
  }

  return payload;
}
